import React from 'react'
import axios from 'axios'
import { API_POOL_BASE_URL } from 'constants/links'
import { Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts'
import { roundToDecimalPlace } from 'utils/data'
import {
  ChartHeader,
  ChartHeaderTitle,
  ChartTitle,
  RechartsWrapper,
  StyledTabButton,
  Tabs,
  TooltipContainer,
} from '../Metrics/styled'
import { LoadingRows } from 'pages/Pool/styleds'

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}
const MetricsTVL = () => {
  const [dailyTVLs, setDailyTVLs] = React.useState<{ date: string; tvlUsd: string }[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [selectedRange, setSelectedRange] = React.useState<'7D' | '30D'>('7D')

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API_POOL_BASE_URL}/api/tvl`)
      setDailyTVLs(response.data.data)
      setLoading(false)
    } catch (error) {
      console.log(error)
    }
  }

  React.useEffect(() => {
    if (loading) {
      fetchMetrics()
    }
  }, [loading])

  const fillMissingDays = (data: { name: string; tvlUsd: number }[], days: number) => {
    if (data.length === 0) return []

    const filledData = [...data]

    const latestDate = new Date(filledData[filledData.length - 1].name)

    const today = new Date()

    for (let i = 1; filledData.length < days; i++) {
      const pastDate = new Date(latestDate)
      pastDate.setDate(latestDate.getDate() - i)

      if (pastDate > today) break

      if (
        !filledData.some(
          (entry) => new Date(entry.name).toISOString().slice(0, 10) === pastDate.toISOString().slice(0, 10)
        )
      ) {
        filledData.push({
          name: pastDate.toISOString(),
          tvlUsd: 0,
        })
      }
    }

    filledData.sort((a, b) => new Date(a.name).getTime() - new Date(b.name).getTime())

    return filledData
  }

  const chartDataRaw = dailyTVLs.map((tvl: { date: string; tvlUsd: string }) => {
    return {
      name: new Date(new Date(tvl.date).setDate(new Date(tvl.date).getDate() - 1)).toISOString(),
      tvlUsd: parseFloat(tvl.tvlUsd),
    }
  })

  const chartData = fillMissingDays(chartDataRaw, 30)
  const chartDataFiltered = selectedRange === '7D' ? chartData.slice(-7) : chartData
  const last24HData = chartDataFiltered[chartDataFiltered.length - 1]

  return (
    <RechartsWrapper>
      {loading ? (
        <>
          <LoadingRows>
            <div />
            <div />
            <div />
            <div />
            <div />
          </LoadingRows>
        </>
      ) : (
        <>
          <ChartHeader>
            <ChartHeaderTitle>
              <ChartTitle>TVL</ChartTitle>
              <Tabs>
                <StyledTabButton onClick={() => setSelectedRange('7D')} isActive={selectedRange === '7D'}>
                  7D
                </StyledTabButton>
                <StyledTabButton onClick={() => setSelectedRange('30D')} isActive={selectedRange === '30D'}>
                  30D
                </StyledTabButton>
              </Tabs>
            </ChartHeaderTitle>
            <ChartTitle>{last24HData ? `${roundToDecimalPlace(`${last24HData.tvlUsd}`, '0')}$` : ''}</ChartTitle>
          </ChartHeader>
          <ResponsiveContainer width="100%" height={185}>
            <AreaChart data={chartDataFiltered}>
              <defs>
                <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4AFB39" stopOpacity={0.7} />
                  <stop offset="100%" stopColor="#4AFB39" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="tvlUsd" stroke="#4AFB39" fill="url(#colorVolume)" />
              <Tooltip
                content={({ payload }) => {
                  if (payload && payload.length > 0) {
                    const data = payload[0].payload
                    return (
                      <TooltipContainer>
                        <span className="tooltip__heading">{formatDate(data.name)}</span>
                        <div className="tooltip__volume">
                          <span className="tooltip__volume-title">TVL</span>
                          <span className="tooltip__volume-value">${roundToDecimalPlace(data.tvlUsd, '0')}</span>
                        </div>
                      </TooltipContainer>
                    )
                  }
                  return null
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </RechartsWrapper>
  )
}

export default MetricsTVL
